import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { GeoJSON } from "react-leaflet";
import type { Layer, LeafletMouseEvent, StyleFunction, GeoJSON as LeafletGeoJSON } from "leaflet";
import type { Feature, Geometry, FeatureCollection } from "geojson";
import L from "leaflet";
import countiesGeoJson from "../GisTab/counties_southeast.json";

// ---------------------------------------------------------------------------
// Legend label decluttering
// ---------------------------------------------------------------------------
export const LEGEND_BAR_HEIGHT = 180; // px — must match .env-legend-row height in CSS
const MIN_LABEL_GAP = 14;             // px — minimum vertical space between label centers

export const declutterTopPositions = (topPercents: number[]): number[] => {
    const raw = topPercents.map(p => (p / 100) * LEGEND_BAR_HEIGHT);

    const order = raw.map((_, i) => i).sort((a, b) => raw[a] - raw[b]);
    const sortedPx = order.map(i => raw[i]);

    for (let i = 1; i < sortedPx.length; i++) {
        if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
            sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
        }
    }
    const overflow = sortedPx[sortedPx.length - 1] - LEGEND_BAR_HEIGHT;
    if (overflow > 0) {
        for (let i = 0; i < sortedPx.length; i++) sortedPx[i] -= overflow;
        for (let i = 1; i < sortedPx.length; i++) {
            if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
                sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
            }
        }
    }
    const clamped = sortedPx.map(v => Math.max(0, Math.min(LEGEND_BAR_HEIGHT, v)));

    const result = new Array(topPercents.length);
    order.forEach((originalIdx, sortedIdx) => {
        result[originalIdx] = clamped[sortedIdx];
    });
    return result;
};

// ---------------------------------------------------------------------------
// Timestamp formatting
// ---------------------------------------------------------------------------
export const formatEasternTimestamp = (raw: string): string => {
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return `${raw} ET`;

    const [, year, month, day, hour, minute] = match;
    const dt = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));

    const datePart = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const timePart = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    return `${datePart}, ${timePart} ET`;
};

// ---------------------------------------------------------------------------
// Value display precision
// ---------------------------------------------------------------------------
// Some variables (e.g. Growing Season Index, ~0.45-0.77) vary only within a
// small decimal range, where rounding to the nearest integer collapses every
// value to the same number and makes the legend/county labels useless.
// Decide precision dynamically from the data's own min/max rather than
// hardcoding a per-variable list, so this keeps working correctly for any
// future variable with a similarly small range.
export const shouldShowDecimals = (min: number | null, max: number | null): boolean => {
    if (min == null || max == null) return false;
    return Math.abs(min) < 5 && Math.abs(max) < 5;
};

export const formatMapValue = (value: number, decimals: boolean): string => {
    return decimals ? value.toFixed(2) : Math.round(value).toString();
};

// ---------------------------------------------------------------------------
// County value marker icon
// ---------------------------------------------------------------------------
export const countyValueIcon = (label: string) => {
    return L.divIcon({
        className: "county-value-icon",
        html: `<div style="
            transform: translate(-50%, -50%);
            display: flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            min-width: 26px;
            height: 20px;
            padding: 2px 6px;
            background: rgba(17, 24, 39, 0.85);
            color: #fff;
            font-size: 11px;
            font-weight: 600;
            line-height: 1;
            border-radius: 4px;
            border: 1px solid rgba(255,255,255,0.6);
            white-space: nowrap;
        ">${label}</div>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
    });
};

// ---------------------------------------------------------------------------
// County borders layer
// ---------------------------------------------------------------------------
interface CountyFeatureProps {
    NAME: string;
    STATEFP: string;
    STATE?: string;
    [key: string]: any;
}

export const CountyBordersLayer: React.FC = () => {
    const geoJsonRef = useRef<LeafletGeoJSON | null>(null);
    const [geoData, setGeoData] = useState<FeatureCollection | null>(null);

    useEffect(() => {
        setGeoData(countiesGeoJson as FeatureCollection);
    }, []);

    const floridaGeoData = useMemo<FeatureCollection | null>(() => {
        if (!geoData) return null;
        return {
            ...geoData,
            features: geoData.features.filter((feature) => {
                const props = feature.properties as CountyFeatureProps;
                const state = props?.STATE ?? "";
                return props?.STATEFP === "12" || state === "Florida" || state === "FL";
            }),
        };
    }, [geoData]);

    const styleFn: StyleFunction<CountyFeatureProps> = useCallback(() => ({
        fillOpacity: 0,
        color: "#374151",
        weight: 1,
        interactive: true,
    }), []);

    const onEachFeature = useCallback((feature: Feature<Geometry, CountyFeatureProps>, layer: Layer) => {
        const name = feature.properties?.NAME ?? "";

        layer.bindTooltip(
            `${name} County`,
            {
                className: "county-tooltip",
                sticky: false,
                direction: "top",
            }
        );

        layer.on({
            mouseover: (e: LeafletMouseEvent) => {
                const target = e.target;
                target.setStyle({ weight: 2.5, color: "#111827" });
                target.bringToFront();
                target.openTooltip();
            },
            mouseout: (e: LeafletMouseEvent) => {
                const target = e.target;
                geoJsonRef.current?.resetStyle(target);
                target.closeTooltip();
            },
        });
    }, []);

    if (!floridaGeoData) return null;

    return (
        <GeoJSON
            ref={geoJsonRef}
            data={floridaGeoData}
            style={styleFn as any}
            onEachFeature={onEachFeature as any}
        />
    );
};